import type { ReactElement, ReactNode, SyntheticEvent } from "react"
import ButtonAtm from "@onestore/hel/dist/components/atoms/ButtonAtm"
import LoaderAtm from "@onestore/hel/dist/components/atoms/LoaderAtm"
import UnderlineLinkAtm from "@onestore/hel/dist/components/atoms/UnderlineLinkAtm"
import ModalWithImageMol from "@onestore/hel/dist/components/molecules/ModalWithImageMol"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import PortalContainerOrg, {
  PortalType,
} from "@onestore/hel/dist/components/organisms/PortalContainerOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import WrapOrg from "@onestore/hel/dist/components/organisms/WrapOrg"
import _t from "@core/i18n"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import type { ChangePeriodModal as ChangePeriodModalType } from "@gatsby-plugin-generic-page/fragments/changePeriodModal"
import getBaseMarkdownOverrides from "@gatsby-plugin-generic-page/helpers/getBaseMarkdownOverrides"
import { Text } from "@ionos-web-design-system/react"
import assetsSquidexToImageObject from "~/lib/assetsSquidexToImageObject"

export type ChangePeriodModalProps = ChangePeriodModalType["flatData"] & {
  isModalOpen: boolean
  onConfirmedAction: () => void
  onClose: (event: SyntheticEvent) => void
  isModalLoading?: boolean
}

export function ChangePeriodModal({
  isModalOpen,
  image,
  content,
  additionalInfo,
  buttonText,
  onClose,
  onConfirmedAction,
  isModalLoading,
}: ChangePeriodModalProps): ReactElement<ChangePeriodModalProps> {
  return (
    <PortalContainerOrg portalType={PortalType.MODAL}>
      <ModalWithImageMol
        image={assetsSquidexToImageObject(image, "")}
        content={
          <>
            {content ? (
              <MarkdownText
                components={{
                  ...getBaseMarkdownOverrides({
                    typography: "medium1_h",
                    boldTypography: "medium3_h",
                    emphasis: "medium",
                    pushSpace: 3,
                  }),
                  h1: ({ children }: { children: ReactNode }) => (
                    <Text asChild variant="heading3xl" className="mb-6">
                      <h1>{children}</h1>
                    </Text>
                  ),
                }}
              >
                {content}
              </MarkdownText>
            ) : null}

            {!isModalLoading ? (
              <>
                <WrapOrg gutterSpace={3} alignCenter="vertical">
                  <ButtonAtm
                    onClick={onConfirmedAction}
                    isWider
                    title={buttonText ? buttonText : _t("domainModal.button")}
                    color="success"
                  >
                    {buttonText ? buttonText : _t("domainModal.button")}
                  </ButtonAtm>

                  <UnderlineLinkAtm
                    title={_t("domainModal.link")}
                    typography="medium3_s"
                    onClick={onClose}
                  >
                    {_t("domainModal.link")}
                  </UnderlineLinkAtm>
                </WrapOrg>

                {additionalInfo ? (
                  <PushOrg topSpace={2}>
                    <MarkdownText
                      components={getBaseMarkdownOverrides({
                        typography: "nano1",
                        boldTypography: "nano3",
                      })}
                    >
                      {additionalInfo}
                    </MarkdownText>
                  </PushOrg>
                ) : null}
              </>
            ) : (
              <AlignContentOrg horizontalAlign="center">
                <LoaderAtm size="small" />
              </AlignContentOrg>
            )}
          </>
        }
        isOpen={isModalOpen}
        onCloseButtonClick={onClose}
        contentPaddingSpace={{ top: 5, bottom: 5, left: 5, right: 4 }}
        notHideOnClose
      />
    </PortalContainerOrg>
  )
}
